import { Form } from "@inertiajs/react"
import { RotateCcw } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * OKS-483 — Brand-side `Approve reopen` confirm dialog. Returns an expired
 * collab (after the creator's `request_reopen`) to its submission-ready state
 * (Open `accepted` / Drop·Promo `delivered`) and notifies the creator.
 *
 * Mirrors `mark-purchased-confirm-dialog.tsx` verbatim — single Inertia
 * `<Form>` POST with hidden `event=approve_reopen`. Rendered ONLY on a
 * `reopen_requested` row gated by `can_approve_reopen` (UX Spec §C).
 */
interface ApproveReopenConfirmDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

export function ApproveReopenConfirmDialog({
  row,
  missionId,
}: ApproveReopenConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="default"
            size="sm"
            aria-label={t("collabs.approve_reopen.aria", { title: ariaTitle })}
            data-testid="brand-mission-collab-approve-reopen-trigger"
          >
            <RotateCcw aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.approve_reopen.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-approve-reopen-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.approve_reopen.title")}</DialogTitle>
          <DialogDescription>
            {t("collabs.approve_reopen.body", { creator: row.creator.name })}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="approve_reopen" />
              {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
              <ReturnToFields />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="brand-mission-collab-approve-reopen-cancel"
                >
                  {t("collabs.approve_reopen.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={processing}
                  autoFocus
                  data-testid="brand-mission-collab-approve-reopen-confirm"
                >
                  {t("collabs.approve_reopen.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
